import { computed, reactive, Ref } from 'vue';
import type { useElementMeasurements } from './useElementMeasurements';
import type { useRAFScheduler } from '../../../../core/composable/useRAFScheduler';

/**
 * Composable для управления цветом текста пунктов меню под индикатором
 * 
 * Решает проблему читаемости текста когда тёмный индикатор проплывает
 * над пунктами с тёмным текстом - динамически меняет цвет текста на светлый
 * при перекрытии более 50%.
 * 
 * @param enabled - Ref для включения/выключения функции
 * @param activeColor - Ref с цветом активного текста (обычно светлый)
 * @param measurementCache - Кеш измерений элементов
 * @param rafScheduler - Планировщик RAF для батчинга обновлений
 * 
 * @example
 * ```ts
 * const textColorManager = useIndicatorTextColor(
 *   toRef(props, 'enableTextColorTransition'),
 *   toRef(props, 'activeColor'),
 *   measurementCache,
 *   rafScheduler
 * );
 * 
 * // При обновлении позиции индикатора
 * textColorManager.updateTextColors({ left: 100, width: 150 });
 * 
 * // Получить цвет для пункта
 * const color = textColorManager.getTextColor('item-1', '#333', false);
 * ```
 */
export function useIndicatorTextColor(
  enabled: Ref<boolean>,
  activeColor: Ref<string>,
  measurementCache: ReturnType<typeof useElementMeasurements>,
  rafScheduler: ReturnType<typeof useRAFScheduler>
) {
  // Состояние перекрытия для каждого пункта (itemId -> процент перекрытия)
  const itemOverlapState = reactive<Map<string | number, number>>(new Map());
  
  // Таймеры для задержки обновления цвета (отдельный для каждого пункта)
  const colorUpdateTimers = new Map<string | number, ReturnType<typeof setTimeout>>();
  
  /**
   * Вычислить процент перекрытия индикатора с пунктом меню
   * 
   * @param indicatorLeft - Левая граница индикатора
   * @param indicatorRight - Правая граница индикатора
   * @param itemLeft - Левая граница пункта
   * @param itemRight - Правая граница пункта
   * @returns Процент перекрытия (0-100)
   */
  const calculateOverlap = (
    indicatorLeft: number,
    indicatorRight: number,
    itemLeft: number,
    itemRight: number
  ): number => {
    // Нет перекрытия - индикатор полностью слева или справа от пункта
    if (indicatorRight < itemLeft || indicatorLeft > itemRight) {
      return 0;
    }
    
    // Вычислить область перекрытия
    const overlapLeft = Math.max(indicatorLeft, itemLeft);
    const overlapRight = Math.min(indicatorRight, itemRight);
    const overlapWidth = overlapRight - overlapLeft;
    
    // Процент от ширины пункта
    const itemWidth = itemRight - itemLeft;
    return itemWidth > 0 ? (overlapWidth / itemWidth) * 100 : 0;
  };
  
  /**
   * Обновить цвета текста на основе текущей позиции индикатора
   * 
   * Использует кешированные позиции элементов и батчит обновления через RAF
   * для минимизации влияния на производительность.
   * 
   * Асимметричная задержка:
   * - 300ms задержка при добавлении белого цвета (индикатор приближается)
   * - Мгновенное возвращение к обычному цвету (индикатор уходит)
   * 
   * @param indicatorPosition - Позиция и размер индикатора
   */
  const updateTextColors = (indicatorPosition: { left: number; width: number }): void => {
    if (!enabled.value) return;
    
    const indicatorLeft = indicatorPosition.left;
    const indicatorRight = indicatorPosition.left + indicatorPosition.width;
    
    // Батчим все обновления цветов через RAF scheduler
    rafScheduler.schedule('update-text-colors', () => {
      const cache = measurementCache.cache.value;
      
      // Проходим по всем пунктам меню
      cache.items.forEach((measurement, itemId) => {
        const itemLeft = measurement.left;
        const itemRight = measurement.left + measurement.width;
        
        // Вычисляем процент перекрытия
        const overlapPercent = calculateOverlap(
          indicatorLeft,
          indicatorRight,
          itemLeft,
          itemRight
        );
        
        const currentOverlap = itemOverlapState.get(itemId) ?? 0;
        const shouldBeWhite = overlapPercent > 50;
        const isCurrentlyWhite = currentOverlap > 50;
        
        // Отменяем предыдущий таймер для этого пункта если есть
        const existingTimer = colorUpdateTimers.get(itemId);
        if (existingTimer) {
          clearTimeout(existingTimer);
          colorUpdateTimers.delete(itemId);
        }
        
        // Если нужно убрать белый цвет (индикатор уходит) - делаем мгновенно
        if (isCurrentlyWhite && !shouldBeWhite) {
          itemOverlapState.set(itemId, overlapPercent);
        }
        // Если нужно добавить белый цвет (индикатор приближается) - с задержкой
        else if (!isCurrentlyWhite && shouldBeWhite) {
          const timer = setTimeout(() => {
            itemOverlapState.set(itemId, overlapPercent);
            colorUpdateTimers.delete(itemId);
          }, 300);
          colorUpdateTimers.set(itemId, timer);
        }
        // В остальных случаях обновляем мгновенно
        else {
          itemOverlapState.set(itemId, overlapPercent);
        }
      });
    }, 'normal');
  };
  
  /**
   * Получить цвет текста для пункта меню
   * 
   * Возвращает activeColor если индикатор перекрывает пункт более чем на 50%,
   * иначе возвращает defaultColor.
   * 
   * @param itemId - ID пункта меню
   * @param defaultColor - Цвет текста по умолчанию
   * @param isActive - Является ли пункт активным
   * @returns Цвет текста для применения
   */
  const getTextColor = (
    itemId: string | number,
    defaultColor: string,
    isActive: boolean
  ): string => {
    // Если функция отключена, возвращаем дефолтный цвет
    if (!enabled.value) return defaultColor;
    
    // Активный пункт всегда использует activeColor
    if (isActive) return activeColor.value;
    
    const overlapPercent = itemOverlapState.get(itemId) ?? 0;
    
    // Применяем activeColor если перекрытие > 50%
    // Порог 50% предотвращает мерцание при частичном перекрытии
    return overlapPercent > 50 ? activeColor.value : defaultColor;
  };
  
  /**
   * Очистка состояния при unmount компонента
   */
  const cleanup = (): void => {
    // Отменяем все pending таймеры
    colorUpdateTimers.forEach(timer => clearTimeout(timer));
    colorUpdateTimers.clear();
    
    itemOverlapState.clear();
  };
  
  /**
   * Computed для проверки наличия перекрытий
   * Полезно для отладки
   */
  const hasOverlaps = computed(() => {
    if (!enabled.value) return false;
    return Array.from(itemOverlapState.values()).some(percent => percent > 0);
  });
  
  return {
    updateTextColors,
    getTextColor,
    cleanup,
    hasOverlaps
  };
}
